import { DebugRepository } from "@/domain/repository/debug-repository";
import { isDebugMode } from "@/utils/environment";
import { SATError, SATResult } from "@sadtx/sat-error";

export enum DebugMaintenanceActionType {
  DeleteGuardianWithPatient = "deleteGuardianWithPatient",
  DeletePatientWithAllData = "deletePatientWithAllData",
  PurgePatient = "purgePatient",
  DeleteActivity = "deleteActivity",
  DeleteCalibration = "deleteCalibration",
  DeleteFriends = "deleteFriends",
  DeleteCurrencyItems = "deleteCurrencyItems",
  DeleteMissions = "deleteMissions",
  DeleteTutorial = "deleteTutorial",
  AddAllTutorials = "addAllTutorials",
  DeleteStory = "deleteStory",
  SetSnExpired = "setSnExpired",
  SetLightEstimationCount = "setLightEstimationCount",
  SetActivityIndex = "setActivityIndex",
}

export type DebugMaintenanceActionPayloadMap = {
  [DebugMaintenanceActionType.DeleteGuardianWithPatient]: { screeningId: string };
  [DebugMaintenanceActionType.DeletePatientWithAllData]: { screeningId: string };
  [DebugMaintenanceActionType.PurgePatient]: { screeningId: string };
  [DebugMaintenanceActionType.DeleteActivity]: { screeningId: string; dateOfDay: string };
  [DebugMaintenanceActionType.DeleteCalibration]: { screeningId: string; dateOfDay: string };
  [DebugMaintenanceActionType.DeleteFriends]: { screeningId: string };
  [DebugMaintenanceActionType.DeleteCurrencyItems]: { screeningId: string };
  [DebugMaintenanceActionType.DeleteMissions]: { screeningId: string };
  [DebugMaintenanceActionType.DeleteTutorial]: { screeningId: string };
  [DebugMaintenanceActionType.AddAllTutorials]: { screeningId: string };
  [DebugMaintenanceActionType.DeleteStory]: { screeningId: string };
  [DebugMaintenanceActionType.SetSnExpired]: { loginId: string };
  [DebugMaintenanceActionType.SetLightEstimationCount]: {
    screeningId: string;
    dateOfDay: string;
    count: number;
  };
  [DebugMaintenanceActionType.SetActivityIndex]: {
    screeningId: string;
    dateOfDay: string;
    sessionIndex: number;
    cycleIndex: number;
  };
};

export type DebugMaintenanceActionRequest = {
  [K in DebugMaintenanceActionType]: {
    type: K;
    payload: DebugMaintenanceActionPayloadMap[K];
  };
}[DebugMaintenanceActionType];

export class DebugMaintenanceUsecase {
  constructor(private readonly debugRepository: DebugRepository) {}

  executeAction(request: DebugMaintenanceActionRequest): Promise<SATResult<void, SATError>> {
    switch (request.type) {
      case DebugMaintenanceActionType.DeleteGuardianWithPatient:
        return this.deleteGuardianWithPatient(request.payload.screeningId);
      case DebugMaintenanceActionType.DeletePatientWithAllData:
        return this.deletePatientWithAllData(request.payload.screeningId);
      case DebugMaintenanceActionType.PurgePatient:
        return this.purgePatient(request.payload.screeningId);
      case DebugMaintenanceActionType.DeleteActivity:
        return this.deleteActivity(request.payload.screeningId, request.payload.dateOfDay);
      case DebugMaintenanceActionType.DeleteCalibration:
        return this.deleteCalibration(request.payload.screeningId, request.payload.dateOfDay);
      case DebugMaintenanceActionType.DeleteFriends:
        return this.deleteFriends(request.payload.screeningId);
      case DebugMaintenanceActionType.DeleteCurrencyItems:
        return this.deleteCurrencyItems(request.payload.screeningId);
      case DebugMaintenanceActionType.DeleteMissions:
        return this.deleteMissions(request.payload.screeningId);
      case DebugMaintenanceActionType.DeleteTutorial:
        return this.deleteTutorial(request.payload.screeningId);
      case DebugMaintenanceActionType.AddAllTutorials:
        return this.addAllTutorials(request.payload.screeningId);
      case DebugMaintenanceActionType.DeleteStory:
        return this.deleteStory(request.payload.screeningId);
      case DebugMaintenanceActionType.SetSnExpired:
        return this.setSnExpired(request.payload.loginId);
      case DebugMaintenanceActionType.SetLightEstimationCount:
        return this.setLightEstimationCount(
          request.payload.screeningId,
          request.payload.dateOfDay,
          request.payload.count,
        );
      case DebugMaintenanceActionType.SetActivityIndex:
        return this.setActivityIndex(
          request.payload.screeningId,
          request.payload.dateOfDay,
          request.payload.sessionIndex,
          request.payload.cycleIndex,
        );
      default:
        return Promise.reject(new Error("지원하지 않는 디버그 액션입니다."));
    }
  }

  private assertDebugEnvironment(): void {
    if (!isDebugMode) {
      throw new Error("디버그 유지보수 기능은 DEV/QA 환경에서만 사용할 수 있습니다.");
    }
  }

  private validateDateOfDay(dateOfDay: string): void {
    const yyyyMmDdRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!yyyyMmDdRegex.test(dateOfDay)) {
      throw new Error("dateOfDay는 yyyy-MM-dd 형식으로 입력해주세요.");
    }

    const [year, month, day] = dateOfDay.split("-").map(Number);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));
    const isValidDate =
      parsedDate.getUTCFullYear() === year &&
      parsedDate.getUTCMonth() === month - 1 &&
      parsedDate.getUTCDate() === day;

    if (!isValidDate) {
      throw new Error("dateOfDay는 유효한 yyyy-MM-dd 날짜로 입력해주세요.");
    }

    const today = new Date();
    const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    if (parsedDate.getTime() > todayUtc.getTime()) {
      throw new Error("dateOfDay는 오늘 날짜 이후로 입력할 수 없습니다.");
    }
  }

  deleteGuardianWithPatient(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteGuardianWithPatient(screeningId);
  }

  deletePatientWithAllData(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deletePatientWithAllData(screeningId);
  }

  purgePatient(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.purgePatient(screeningId);
  }

  deleteActivity(screeningId: string, dateOfDay: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    this.validateDateOfDay(dateOfDay);
    return this.debugRepository.deleteActivity(screeningId, dateOfDay);
  }

  deleteCalibration(
    screeningId: string,
    dateOfDay: string,
  ): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    this.validateDateOfDay(dateOfDay);
    return this.debugRepository.deleteCalibration(screeningId, dateOfDay);
  }

  deleteFriends(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteFriends(screeningId);
  }

  deleteCurrencyItems(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteCurrencyItems(screeningId);
  }

  deleteMissions(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteMissions(screeningId);
  }

  deleteTutorial(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteTutorial(screeningId);
  }

  addAllTutorials(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.addAllTutorials(screeningId);
  }

  deleteStory(screeningId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.deleteStory(screeningId);
  }

  setSnExpired(loginId: string): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    return this.debugRepository.setSnExpired(loginId);
  }

  setLightEstimationCount(
    screeningId: string,
    dateOfDay: string,
    count: number,
  ): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    this.validateDateOfDay(dateOfDay);

    if (count < 0 || count > 3) {
      return Promise.reject(new Error("count 값은 0에서 3 사이(포함)여야 합니다."));
    }

    return this.debugRepository.setLightEstimationCount(screeningId, dateOfDay, count);
  }

  setActivityIndex(
    screeningId: string,
    dateOfDay: string,
    sessionIndex: number,
    cycleIndex: number,
  ): Promise<SATResult<void, SATError>> {
    this.assertDebugEnvironment();
    this.validateDateOfDay(dateOfDay);

    if (sessionIndex < 0 || sessionIndex > 5) {
      return Promise.reject(new Error("sessionIndex 값은 0에서 5 사이(포함)여야 합니다."));
    }

    if (cycleIndex < 0 || cycleIndex > 4) {
      return Promise.reject(new Error("cycleIndex 값은 0에서 4 사이(포함)여야 합니다."));
    }

    return this.debugRepository.setActivityIndex(screeningId, dateOfDay, sessionIndex, cycleIndex);
  }
}
